import { formatDateTime, formatRelative, readySchema, type ReadinessCheck } from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import { Database, HardDrive, RefreshCw, Server } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { apiRequest } from '@/lib/api/client';
import { env } from '@/lib/env';

export const systemStatusQuery = {
  queryKey: ['system', 'ready'] as const,
  // 503 é uma resposta válida: a API está de pé e informa qual dependência falhou.
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest('/ready', { schema: readySchema, acceptStatus: [503], signal }),
  refetchInterval: 30_000,
};

const SERVICES: { key: 'database' | 'storage'; label: string; description: string; icon: LucideIcon }[] = [
  {
    key: 'database',
    label: 'Banco de dados',
    description: 'Objetivos, indicadores, metas, resultados e histórico.',
    icon: Database,
  },
  {
    key: 'storage',
    label: 'Armazenamento de arquivos',
    description: 'Anexos, fotos, evidências e documentos.',
    icon: HardDrive,
  },
];

export function SystemStatusPage() {
  const query = useQuery(systemStatusQuery);

  return (
    <PageContainer>
      <PageHeader
        title="Status do sistema"
        description="Saúde dos serviços que sustentam a plataforma. Atualiza sozinho a cada 30 segundos."
        actions={
          <Button
            variant="outline"
            onClick={() => void query.refetch()}
            loading={query.isFetching}
            disabled={query.isPending}
          >
            {query.isFetching ? null : <RefreshCw aria-hidden />}
            Verificar agora
          </Button>
        }
      />

      <div className="mt-8">
        {query.isPending ? (
          <StatusSkeleton />
        ) : query.isError ? (
          <div className="rounded-lg border border-border">
            <ErrorState
              error={query.error}
              title="A API não respondeu"
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
            />
          </div>
        ) : (
          <>
            <OverallBanner ok={query.data.status === 'ok'} checkedAt={query.dataUpdatedAt} />
            <ul className="mt-6 divide-y divide-border rounded-lg border border-border bg-surface-raised">
              <ServiceRow
                icon={Server}
                label="API"
                description={env.apiUrl}
                check={{ status: 'ok', latencyMs: Number.NaN }}
              />
              {SERVICES.map((s) => (
                <ServiceRow
                  key={s.key}
                  icon={s.icon}
                  label={s.label}
                  description={s.description}
                  check={query.data.checks[s.key]}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </PageContainer>
  );
}

function OverallBanner({ ok, checkedAt }: { ok: boolean; checkedAt: number }) {
  return (
    <div
      role="status"
      className={
        ok
          ? 'flex flex-col gap-1 rounded-lg bg-status-green-bg px-5 py-4 text-status-green-fg sm:flex-row sm:items-center sm:justify-between'
          : 'flex flex-col gap-1 rounded-lg bg-status-red-bg px-5 py-4 text-status-red-fg sm:flex-row sm:items-center sm:justify-between'
      }
    >
      <p className="text-base font-bold">
        {ok ? 'Todos os serviços estão operando' : 'Há serviços indisponíveis'}
      </p>
      <p className="text-sm" title={formatDateTime(checkedAt)}>
        Verificado {formatRelative(checkedAt)}
      </p>
    </div>
  );
}

function ServiceRow({
  icon: Icon,
  label,
  description,
  check,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  check: ReadinessCheck;
}) {
  const ok = check.status === 'ok';
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-4 sm:flex-nowrap sm:px-5">
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{label}</p>
        <p className="text-sm break-words text-muted-foreground">{description}</p>
      </div>
      <div className="flex basis-full items-center gap-3 pl-13 sm:basis-auto sm:justify-end sm:pl-0">
        {Number.isFinite(check.latencyMs) ? (
          <span className="tabular text-sm text-muted-foreground">{check.latencyMs} ms</span>
        ) : null}
        <StatusBadge
          status={ok ? 'green' : 'red'}
          label={ok ? 'Operando' : `Indisponível${check.error ? ` (${check.error})` : ''}`}
        />
      </div>
    </li>
  );
}

function StatusSkeleton() {
  return (
    <div role="status" aria-label="Verificando serviços">
      <Skeleton className="h-16 rounded-lg" />
      <div className="mt-6 divide-y divide-border rounded-lg border border-border">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 px-5 py-4">
            <Skeleton className="size-10 shrink-0" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-64 max-w-full" />
            </div>
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
